import { useCallback, useEffect, useState } from "react";
import { noticeApi } from '@/features/notice/api/noticeApi';
import {
    CreateNoticeRequestDto,
    Notice,
    noticeListFromDto,
    UpdateNoticeRequestDto,
} from '@/features/notice/types/notice.types';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";

export const useNoticeData = () => {
    const { showToast } = useToast();
    const [allNotices, setAllNotices] = useState<Notice[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchNotices = useCallback(async () => {
        try {
            setLoading(true);
            const response = await noticeApi.getNoticeList();
            setAllNotices(noticeListFromDto(response));
        } catch {
            showToast('공지사항 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setAllNotices([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const createNotice = useCallback(async (request: CreateNoticeRequestDto): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await noticeApi.createNotice(request);
            if (response.success) {
                showToast('공지사항이 등록되었습니다.', { type: ToastType.SUCCESS });
                await fetchNotices();
                return true;
            }
            showToast(response.message || '공지사항 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } catch {
            showToast('공지사항 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchNotices]);

    const updateNotice = useCallback(async (request: UpdateNoticeRequestDto): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await noticeApi.updateNotice(request);
            if (response.success) {
                showToast('공지사항이 수정되었습니다.', { type: ToastType.SUCCESS });
                await fetchNotices();
                return true;
            }
            showToast(response.message || '공지사항 수정에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } catch {
            showToast('공지사항 수정에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchNotices]);

    const deleteNotice = useCallback(async (noticeId: number): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await noticeApi.deleteNotice(noticeId);
            if (response.success) {
                showToast('공지사항이 삭제되었습니다.', { type: ToastType.SUCCESS });
                await fetchNotices();
                return true;
            }
            showToast(response.message || '공지사항 삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } catch {
            showToast('공지사항 삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchNotices]);

    useEffect(() => {
        fetchNotices();
    }, [fetchNotices]);

    return { allNotices, loading, fetchNotices, createNotice, updateNotice, deleteNotice };
};
